import type {
  TenantAiProviderConnectionRecordDto,
} from "@/lib/api/generated/control-api.schemas";
import type { AiProviderMetadata } from "./provider-metadata.js";

export type ProviderDraft = {
  id: string;
  label: string;
  profileId: string;
  apiKey: string;
  modelIdsText: string;
};

export type ProviderOnboardingConfig = {
  primaryActionLabel: string;
  primaryActionKind: "save" | "save_and_validate";
  summary: string;
  steps: string[];
  primaryCredentialLabel?: string;
  primaryCredentialHint?: string;
  primaryReadOnlyLabel?: string;
  primaryReadOnlyValue?: string;
  showPrimaryCredentialInput: boolean;
  showModelListInput: boolean;
  showLabelInput: boolean;
  showAdvancedByDefault?: boolean;
  recommendedConnectionId: string;
  recommendedProfileId: string;
};

export function createProviderDraft(
  tenantId: string,
  metadata: AiProviderMetadata,
  existing: TenantAiProviderConnectionRecordDto | null,
): ProviderDraft {
  if (existing) {
    return {
      id: existing.id,
      label: existing.label,
      profileId: existing.profileId,
      apiKey: "",
      modelIdsText: formatModelsText(existing),
    };
  }
  const onboarding = getProviderOnboardingConfig(tenantId, metadata);
  return {
    id: onboarding.recommendedConnectionId,
    label: metadata.label,
    profileId: onboarding.recommendedProfileId,
    apiKey: "",
    modelIdsText: "",
  };
}

export function getProviderOnboardingConfig(
  tenantId: string,
  metadata: AiProviderMetadata,
): ProviderOnboardingConfig {
  const recommendedConnectionId = `provider_${metadata.providerId.replace(/[^a-z0-9_-]+/gi, "_")}`;
  const recommendedProfileId =
    metadata.providerId === "openai-codex"
      ? `openai-codex:${tenantId.replace(/[^a-z0-9_-]+/gi, "-")}`
      : metadata.providerId;

  if (metadata.providerId === "openai-codex") {
    return {
      primaryActionLabel: "Use Codex from this machine",
      primaryActionKind: "save",
      summary:
        "Import the local Codex login from this machine and persist a tenant-scoped OpenAI Codex credential in Postgres.",
      steps: [
        "Sign in with Codex locally if needed.",
        "Import that local login for this tenant.",
        "Tenant runtimes hydrate that tenant credential from the control plane for conversations, channels, and agents.",
      ],
      primaryReadOnlyLabel: "Runtime profile",
      primaryReadOnlyValue: recommendedProfileId,
      showPrimaryCredentialInput: false,
      showModelListInput: false,
      showLabelInput: false,
      recommendedConnectionId,
      recommendedProfileId,
    };
  }

  return {
    primaryActionLabel: existingActionLabel(metadata.authMode),
    primaryActionKind: "save",
    summary:
      metadata.authMode === "api_key"
        ? "Save the provider API key in Postgres, then publish the provider mapping and model catalog for this tenant."
        : "Use the recommended runtime profile mapping for this provider, then validate it before traffic is routed to it.",
    steps:
      metadata.authMode === "api_key"
        ? [
            "Paste the provider API key.",
            "Optional: list the models you want exposed to agent policy selection.",
            "Save and validate the tenant connection.",
          ]
        : [
            "Save the provider mapping for this tenant.",
            "Validate it before routing traffic to this provider.",
          ],
    primaryCredentialLabel: "API key",
    primaryCredentialHint: "Stored tenant-scoped in Postgres and hydrated into the tenant runtime.",
    showPrimaryCredentialInput: metadata.authMode === "api_key",
    showModelListInput: metadata.authMode === "api_key",
    showLabelInput: false,
    primaryReadOnlyLabel: metadata.authMode === "oauth" ? "Runtime profile" : undefined,
    primaryReadOnlyValue: metadata.authMode === "oauth" ? recommendedProfileId : undefined,
    recommendedConnectionId,
    recommendedProfileId,
  };
}

export function toConnectionRecord(
  tenantId: string,
  metadata: AiProviderMetadata,
  draft: ProviderDraft,
  existing: TenantAiProviderConnectionRecordDto | null,
): TenantAiProviderConnectionRecordDto {
  const profileId = draft.profileId.trim();
  const modelIds = parseModelIds(draft.modelIdsText);
  const modelEntries = buildProviderModelMetadataEntries({
    providerId: metadata.providerId,
    modelIds,
    existing,
  });
  return {
    id: draft.id.trim(),
    providerId: metadata.providerId,
    providerGroupId: metadata.providerGroupId,
    label: draft.label.trim(),
    authMode: metadata.authMode,
    status: existing?.status ?? (metadata.authMode === "api_key" ? "active" : "disconnected"),
    credentialsRef: {
      provider: "control_plane",
      path: `${tenantId.trim()}/${metadata.providerGroupId}/${profileId}`,
    },
    profileId,
    metadata: {
      ...(existing?.metadata ?? {}),
      capabilityType: "ai_model",
      ...(modelEntries.length > 0
        ? {
            models: modelEntries,
          }
        : {}),
    },
    ...(existing?.lastValidatedAt ? { lastValidatedAt: existing.lastValidatedAt } : {}),
    ...(existing?.lastError ? { lastError: existing.lastError } : {}),
  };
}

function existingActionLabel(authMode: AiProviderMetadata["authMode"]): string {
  return authMode === "api_key" ? "Save connection" : "Save mapping";
}

function parseModelIds(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\n,]+/g)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );
}

function buildProviderModelMetadataEntries(params: {
  providerId: string;
  modelIds: string[];
  existing: TenantAiProviderConnectionRecordDto | null;
}): Array<Record<string, unknown>> {
  const existingById = new Map(
    readExistingModelEntries(params.existing).map((entry) => [
      normalizeModelId(params.providerId, String(entry.id ?? "")),
      entry,
    ]),
  );

  return params.modelIds.map((rawId) => {
    const id = normalizeModelId(params.providerId, rawId);
    const existingEntry = existingById.get(id);
    return compactModelEntry({
      id,
      name: readString(existingEntry?.name),
      alias: readString(existingEntry?.alias),
      contextWindow: readPositiveNumber(existingEntry?.contextWindow),
      maxTokens: readPositiveNumber(existingEntry?.maxTokens),
      reasoning: readBoolean(existingEntry?.reasoning),
      input: readStringArray(existingEntry?.input),
    });
  });
}

function normalizeModelId(providerId: string, value: string): string {
  const trimmed = value.trim();
  const prefix = `${providerId.trim().toLowerCase()}/`;
  return trimmed.toLowerCase().startsWith(prefix) ? trimmed.slice(prefix.length).trim() : trimmed;
}

function readExistingModelEntries(
  existing: TenantAiProviderConnectionRecordDto | null,
): Array<Record<string, unknown>> {
  const metadata =
    existing?.metadata && typeof existing.metadata === "object" && !Array.isArray(existing.metadata)
      ? (existing.metadata as Record<string, unknown>)
      : null;
  return Array.isArray(metadata?.models)
    ? metadata.models.filter(
        (entry): entry is Record<string, unknown> =>
          Boolean(entry) && typeof entry === "object" && !Array.isArray(entry),
      )
    : [];
}

function compactModelEntry(entry: {
  id: string;
  name?: string;
  alias?: string;
  contextWindow?: number;
  maxTokens?: number;
  reasoning?: boolean;
  input?: string[];
}): Record<string, unknown> {
  const result: Record<string, unknown> = { id: entry.id };
  if (entry.name) result.name = entry.name;
  if (entry.alias) result.alias = entry.alias;
  if (entry.contextWindow) result.contextWindow = entry.contextWindow;
  if (entry.maxTokens) result.maxTokens = entry.maxTokens;
  if (entry.reasoning !== undefined) result.reasoning = entry.reasoning;
  if (entry.input?.length) result.input = entry.input;
  return result;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readPositiveNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const entries = value.filter((entry): entry is string => typeof entry === "string" && entry.trim());
  return entries.length > 0 ? Array.from(new Set(entries.map((entry) => entry.trim()))) : undefined;
}

function formatModelsText(existing: TenantAiProviderConnectionRecordDto): string {
  return readExistingModelEntries(existing)
    .map((entry) =>
      entry && typeof entry === "object" && !Array.isArray(entry) && typeof entry.id === "string"
        ? entry.id.trim()
        : "",
    )
    .filter(Boolean)
    .join(", ");
}
